import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineArrowLeft,
  AiOutlineEye,
  AiOutlinePlus,
  AiOutlineReload,
  AiOutlineShareAlt,
  AiOutlineCamera,
  AiOutlineLink,
  AiOutlineInbox, AiOutlineEnvironment, AiOutlineFileText, AiOutlineDownload,
  AiOutlineVideoCamera } from 'react-icons/ai';
import ZoomableImage from './ZoomableImage';
import ProjectNotes from './ProjectNotes';
import { apiUrl } from '../config/api';
import { readVideo, formatDuration, isVideoFile } from '../lib/videoPoster';
import { uploadVideo, VIDEO_MAX_BYTES } from '../lib/videoUpload';
import { useAppSettings } from '../lib/appSettings';
import './CompanyCamPage.css';

const formatDateDisplay = (dateString) => {
  if (!dateString || dateString === 'Undated') return 'Undated';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

/**
 * Google Maps directions for an address. Signed-in only: the public customer
 * timeline deliberately does not link out, so a shared link can't be used to
 * navigate to somebody's home.
 *
 * Uses the universal maps URL, which opens the native app on iOS/Android and the
 * web map on desktop, rather than a platform-specific scheme.
 */
/**
 * Hand this project to Reportify's report builder.
 *
 * Reportify pulls the photos itself using a Capturify API key held on its
 * server, so no public share link is created - these photos carry customers'
 * names, addresses and phone numbers, and a report shouldn't cost them a
 * permanent public URL.
 */
const REPORTIFY_URL = (process.env.REACT_APP_REPORTIFY_URL || 'https://reportify.chimneysweeps.com').replace(/\/$/, '');

function reportifyUrl(clientId, handoffToken) {
  return `${REPORTIFY_URL}/?capturifyProject=${encodeURIComponent(clientId)}`
    + `&t=${encodeURIComponent(handoffToken)}`;
}

function mapsUrl(address) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

const CompanyCamPage = ({ user, onOpenCamera }) => {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editClientName, setEditClientName] = useState('');
  const [editClientAddress, setEditClientAddress] = useState('');
  const [editCompanycamUrl, setEditCompanycamUrl] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  // Short-lived, project-scoped token that lets Reportify read this project.
  // Only admins and managers can mint one, so it is what actually enforces the
  // restriction - Reportify has no roles of its own.
  const [reportToken, setReportToken] = useState('');
  const [clientImages, setClientImages] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);
  const { videoEnabled } = useAppSettings();
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadClientName, setUploadClientName] = useState('');
  const [uploadClientAddress, setUploadClientAddress] = useState('');
  const [uploadInspectionDate, setUploadInspectionDate] = useState('');
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const geocodeDebounceRef = useRef(null);
  const geocodeRequestIdRef = useRef(0);
  const [imageNotes, setImageNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [notesModal, setNotesModal] = useState({ isOpen: false, imageId: null, image: null });
  const [clientSearch, setClientSearch] = useState('');
  const [clientSortBy, setClientSortBy] = useState('name');
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientsActiveTotal, setClientsActiveTotal] = useState(0);
  const [clientsBackupTotal, setClientsBackupTotal] = useState(0);
  const [loadingMoreClients, setLoadingMoreClients] = useState(false);
  // False until the first list response lands. The "No projects yet" panel is gated on
  // this: without it the empty state paints on mount, before the request has even been
  // answered, and every account with projects sees "No projects yet" flash first.
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const clientSearchDebounceRef = useRef(null);
  const CLIENTS_PAGE_SIZE = 10;
  const [uploadStatuses, setUploadStatuses] = useState({});
  const [refreshingClients, setRefreshingClients] = useState(false);
  const [refreshingImages, setRefreshingImages] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMainPhotoPicker, setShowMainPhotoPicker] = useState(false);

  const token = localStorage.getItem('token');

  const readApiResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return response.json();
    }
    const text = await response.text();
    if (!response.ok) {
      throw new Error(
        `API ${response.status}: ${text.slice(0, 140) || 'Non-JSON response from server'}`
      );
    }
    throw new Error('Server returned non-JSON success response');
  };

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Mint the Reportify handoff token for whichever project is open. Technicians
  // never get one (the API refuses), which is why the button is theirs to see.
  useEffect(() => {
    const isStaff = user?.isAdmin || user?.isManager;
    if (!selectedClient?.id || !isStaff) {
      setReportToken('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(apiUrl('/api/scraper/report-handoff'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ clientId: selectedClient.id }),
        });
        const data = await readApiResponse(response);
        if (!cancelled && response.ok) setReportToken(data.token || '');
      } catch {
        // Non-fatal: without a token the button just doesn't appear.
        if (!cancelled) setReportToken('');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id, user]);

  // Search/sort now happen server-side (so "Load More" keeps paging through the same filtered
  // set) — debounced so typing a search term doesn't fire a request per keystroke. Skipped on
  // the very first render since the mount effect above already loads page 1.
  const isFirstClientFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstClientFilterRender.current) {
      isFirstClientFilterRender.current = false;
      return undefined;
    }
    if (clientSearchDebounceRef.current) clearTimeout(clientSearchDebounceRef.current);
    clientSearchDebounceRef.current = setTimeout(() => {
      loadClients();
    }, 300);
    return () => clearTimeout(clientSearchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSearch, clientSortBy]);

  useEffect(() => {
    if (!clientId || selectedClient?.id === parseInt(clientId)) return;
    const client = clients.find((c) => c.id === parseInt(clientId));
    if (client) {
      handleClientClick(client);
    } else if (clients.length > 0) {
      // Not on whatever page is currently loaded — fetch it directly rather than requiring
      // every page to be paged through first.
      loadClientById(clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clients]);

  // Poll background rehost progress so client rows / the timeline header can show
  // "Uploading N/total" while scraped images are still being saved to S3 + Postgres.
  useEffect(() => {
    const pollStatus = async () => {
      try {
        const response = await fetch(apiUrl('/api/scraper/upload-status'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await readApiResponse(response);
        if (response.ok) {
          const map = {};
          (data.jobs || []).forEach((job) => { map[job.clientId] = job; });
          setUploadStatuses(map);
        }
      } catch (err) {
        // Best-effort polling — a failed check just tries again next tick.
      }
    };
    pollStatus();
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Loads clients 10 at a time from the server (search + sort happen server-side too, so
  // "Load More" always continues the same filtered/sorted set the user is looking at).
  // `reset: true` (the default) replaces the list from offset 0 — used on mount and whenever
  // search/sort changes; `reset: false` (Load More) appends the next page.
  // Each load gets a sequence number; only the newest one may update the list, so a slow
  // earlier search can't land after (and overwrite) the results for what's typed now.
  const clientsLoadSeqRef = useRef(0);
  const loadClients = async ({ reset = true } = {}) => {
    const seq = ++clientsLoadSeqRef.current;
    const offset = reset ? 0 : clients.length;
    if (!reset) setLoadingMoreClients(true);
    try {
      const params = new URLSearchParams({
        limit: String(CLIENTS_PAGE_SIZE),
        offset: String(offset),
        sortBy: clientSortBy,
      });
      if (clientSearch.trim()) params.set('search', clientSearch.trim());

      const response = await fetch(apiUrl(`/api/scraper/clients?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await readApiResponse(response);
      if (seq !== clientsLoadSeqRef.current) return;
      if (response.ok) {
        setClients((prev) => (reset ? (data.clients || []) : [...prev, ...(data.clients || [])]));
        setClientsTotal(data.totalCount ?? 0);
        setClientsActiveTotal(data.activeCount ?? 0);
        setClientsBackupTotal(data.backupCount ?? 0);
      } else {
        setError(data.message || 'Failed to load clients');
      }
    } catch (err) {
      if (seq !== clientsLoadSeqRef.current) return;
      setError(err.message || 'Failed to load clients');
      console.error('Error loading clients:', err);
    } finally {
      if (!reset) setLoadingMoreClients(false);
      // Only the newest load may flip this, so a slow earlier request can't declare
      // the list "loaded" while a fresher one is still in flight.
      if (seq === clientsLoadSeqRef.current) setClientsLoaded(true);
    }
  };

  const handleLoadMoreClients = () => loadClients({ reset: false });

  // Resolves a client-detail deep link (e.g. a shared "Project Link") whose target client isn't
  // in whatever page of the list happens to be loaded — fetched directly instead of requiring
  // every page to be paged through first.
  const loadClientById = async (id) => {
    try {
      const response = await fetch(apiUrl(`/api/scraper/clients/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await readApiResponse(response);
      if (response.ok && data.client) {
        handleClientClick(data.client);
      }
    } catch (err) {
      console.error('Error loading client:', err);
    }
  };

  const handleRefreshClients = async () => {
    setRefreshingClients(true);
    await loadClients();
    setRefreshingClients(false);
  };

  // Saves the original, full-size photo. The API hands back a short-lived S3 link that is
  // served as an attachment, so the browser downloads it (a plain <a download> to another
  // origin would just open the image instead).
  const handleDownloadImage = async (image) => {
    if (downloadingId) return;
    setDownloadingId(image.id);
    try {
      const response = await fetch(apiUrl(`/api/scraper/images/${encodeURIComponent(image.id)}/download`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.detail || 'Failed to download photo');
      const link = document.createElement('a');
      link.href = data.url;
      link.download = data.filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(err.message || 'Failed to download photo');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteImage = async (image) => {
    const what = image.mediaType === 'video' ? 'video' : 'photo';
    if (!window.confirm(`Remove this ${what} from the project? It will no longer appear here.`)) return;
    try {
      const response = await fetch(apiUrl('/api/scraper/hide-image'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageId: image.id }),
      });
      const data = await readApiResponse(response);
      if (!response.ok) { setError(data.message || `Failed to remove ${what}`); return; }
      setMessage(what === 'video' ? 'Video removed' : 'Photo removed');
      await loadClients();
      // Re-read this project from the server, not from the list loaded before the
      // removal: that copy still carries the old main photo.
      if (selectedClient) await loadClientById(selectedClient.id);
    } catch (err) {
      setError(err.message || `Failed to remove ${what}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Toggles this image as the client's "house photo" — the cover/profile picture used as its
  // thumbnail wherever it's shown as a card (Home's Projects row, the client-list avatar).
  // Sets a client's main/profile photo — shown as its hero image on the detail page, on its
  // public timeline, and as its card thumbnail on the Home screen's Projects row.
  const handleSelectMainPhoto = async (image) => {
    try {
      const response = await fetch(apiUrl('/api/scraper/set-cover-image'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: image.clientId, imageId: image.id }),
      });
      const data = await readApiResponse(response);
      if (!response.ok) { setError(data.message || 'Failed to set main photo'); return; }

      setSelectedClient((prev) => (prev ? { ...prev, thumbnailUrl: image.imageUrl } : prev));
      setShowMainPhotoPicker(false);
      setMessage('Main photo updated');
      await loadClients();
    } catch (err) {
      setError(err.message || 'Failed to set main photo');
      console.error(err);
    }
  };

  const handleNoteChange = (imageId, noteText) => {
    setImageNotes((prev) => ({ ...prev, [imageId]: noteText.substring(0, 500) }));
  };

  const handleSaveNote = async (imageId) => {
    setSavingNotes((prev) => ({ ...prev, [imageId]: true }));
    try {
      const response = await fetch(apiUrl('/api/scraper/image-note'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageId, note: imageNotes[imageId] || '' }),
      });
      const data = await readApiResponse(response);
      if (!response.ok) { setError(data.message || 'Failed to save note'); return; }
      setMessage('Note saved successfully');
    } catch (err) {
      setError(err.message || 'Failed to save note');
      console.error(err);
    } finally {
      setSavingNotes((prev) => ({ ...prev, [imageId]: false }));
    }
  };

  const loadImageNote = async (imageId) => {
    try {
      const response = await fetch(apiUrl(`/api/scraper/image-note/${imageId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await readApiResponse(response);
      setImageNotes((prev) => ({ ...prev, [imageId]: data.note || '' }));
    } catch (err) {
      console.error('Error loading note:', err);
    }
  };

  // Backup photos arrive with their (read-only) note inline; Captureify photos load theirs lazily.
  const isBackupImage = (image) => image.source === 'backup';
  const noteFor = (image) => (isBackupImage(image) ? image.notes : imageNotes[image.id]);

  const openNotesModal = async (image) => {
    setNotesModal({ isOpen: true, imageId: image.id, image });
    if (!imageNotes[image.id]) await loadImageNote(image.id);
  };

  const closeNotesModal = () => {
    setNotesModal({ isOpen: false, imageId: null, image: null });
    setMessage('');
    setError('');
  };

  const saveNoteFromModal = async () => {
    if (notesModal.imageId) {
      await handleSaveNote(notesModal.imageId);
      // Close modal on success
      if (!error) {
        setTimeout(() => closeNotesModal(), 1000);
      }
    }
  };

  // Client name/address "history" suggestions used to be filtered straight out of the in-memory
  // `clients` array, back when it always held every client. Now that the client-manager list is
  // paginated 10-at-a-time, `clients` may only hold a small loaded slice, so both suggestion
  // sources query the server directly instead (same /api/scraper/clients search the list itself
  // uses), each debounced so typing doesn't fire a request per keystroke.
  const nameSuggestDebounceRef = useRef(null);
  const nameSuggestRequestIdRef = useRef(0);
  // The client rows behind the last fetched suggestions — kept alongside the suggestion labels
  // so selecting one can still auto-fill the other field, without depending on the (now
  // paginated, possibly not containing this client) `clients` list.
  const suggestionClientsRef = useRef([]);

  const handleClientNameChange = (value) => {
    setUploadClientName(value);
    if (nameSuggestDebounceRef.current) clearTimeout(nameSuggestDebounceRef.current);

    if (!value.trim()) {
      setNameSuggestions([]);
      setShowNameSuggestions(false);
      return;
    }

    setShowNameSuggestions(true);
    const requestId = ++nameSuggestRequestIdRef.current;
    nameSuggestDebounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          apiUrl(`/api/scraper/clients?search=${encodeURIComponent(value)}&limit=5&sortBy=name`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await readApiResponse(response);
        if (requestId !== nameSuggestRequestIdRef.current) return; // a newer keystroke superseded this request
        if (response.ok) {
          suggestionClientsRef.current = data.clients || [];
          const filtered = (data.clients || [])
            .map((c) => c.clientName)
            .filter((v, i, a) => a.indexOf(v) === i);
          setNameSuggestions(filtered);
        }
      } catch {
        // Non-fatal — the input itself still works without suggestions.
      }
    }, 250);
  };

  // Merges two sources into one dropdown: addresses we've already used for this account's own
  // clients ("history" — shown first since "we've been here before" beats a generic geocoder
  // hit), and live real-world address completions from OpenStreetMap's Nominatim — both
  // debounced so we don't fire a request on every keystroke.
  const handleClientAddressChange = (value) => {
    setUploadClientAddress(value);

    if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);

    if (!value.trim()) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setShowAddressSuggestions(true);

    const requestId = ++geocodeRequestIdRef.current;
    geocodeDebounceRef.current = setTimeout(async () => {
      try {
        const [historyResponse, geocodeResponse] = await Promise.all([
          fetch(apiUrl(`/api/scraper/clients?search=${encodeURIComponent(value)}&limit=8`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(apiUrl(`/api/geocode/autocomplete?q=${encodeURIComponent(value)}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (requestId !== geocodeRequestIdRef.current) return; // a newer keystroke superseded this request

        const historyData = await readApiResponse(historyResponse).catch(() => ({}));
        const matchingHistoryClients = (historyData.clients || [])
          .filter((c) => c.clientAddress && c.clientAddress.toLowerCase().includes(value.toLowerCase()));
        suggestionClientsRef.current = matchingHistoryClients;
        const historyMatches = matchingHistoryClients
          .map((c) => c.clientAddress)
          .filter((v, i, a) => a.indexOf(v) === i)
          .slice(0, 3)
          .map((label) => ({ label, source: 'history' }));

        const historyLabels = new Set(historyMatches.map((s) => s.label.toLowerCase()));
        const geocodeData = await readApiResponse(geocodeResponse).catch(() => ({}));
        const geocodeMatches = (geocodeData.suggestions || [])
          .filter((label) => !historyLabels.has(label.toLowerCase()))
          .slice(0, 5)
          .map((label) => ({ label, source: 'geocode' }));

        setAddressSuggestions([...historyMatches, ...geocodeMatches]);
      } catch {
        // Non-fatal — history/geocode suggestions just won't appear this keystroke.
      }
    }, 350);
  };

  const handleSelectNameSuggestion = (name) => {
    setUploadClientName(name);
    setShowNameSuggestions(false);
    const matchingClient = suggestionClientsRef.current.find((c) => c.clientName === name);
    if (matchingClient && !uploadClientAddress) {
      setUploadClientAddress(matchingClient.clientAddress || '');
    }
  };

  const handleSelectAddressSuggestion = (suggestion) => {
    const address = suggestion.label;
    setUploadClientAddress(address);
    setShowAddressSuggestions(false);
    const matchingClient = suggestionClientsRef.current.find((c) => c.clientAddress === address);
    if (matchingClient && !uploadClientName) {
      setUploadClientName(matchingClient.clientName || '');
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const filesToUpload = uploadFiles.length > 0 ? uploadFiles : (uploadFile ? [uploadFile] : []);
      if (filesToUpload.length === 0) {
        setError('Please select at least one image file');
        setLoading(false);
        return;
      }
      if (!uploadClientName && !selectedClient) {
        setError('Please select or create a client');
        setLoading(false);
        return;
      }
      if (!uploadInspectionDate) {
        setError('Please select an inspection date');
        setLoading(false);
        return;
      }

      const successCount = { count: 0 };
      const failedFiles = [];

      for (const file of filesToUpload) {
        if (isVideoFile(file)) {
          if (!videoEnabled) { failedFiles.push(`${file.name} (videos coming soon)`); continue; }
          if (file.size > VIDEO_MAX_BYTES) { failedFiles.push(`${file.name} (over 2 GB)`); continue; }
          try {
            const { posterBlob, durationMs } = await readVideo(file);
            await uploadVideo({
              id: `form-${Date.now()}-${file.name}`,
              file,
              posterBlob,
              durationMs,
              target: {
                clientId: selectedClient ? selectedClient.id : '',
                clientName: uploadClientName || selectedClient?.clientName,
                clientAddress: uploadClientAddress || selectedClient?.clientAddress,
                inspectionDate: uploadInspectionDate || '',
              },
              token,
              onProgress: (f) => setMessage(`Uploading ${file.name}… ${Math.round(f * 100)}%`),
            });
            successCount.count++;
          } catch (err) {
            failedFiles.push(file.name);
          }
          continue;
        }
        try {
          const formData = new FormData();
          formData.append('image', file);
          formData.append('clientId', selectedClient ? selectedClient.id : '');
          formData.append('clientName', uploadClientName || selectedClient.clientName);
          formData.append('clientAddress', uploadClientAddress || selectedClient.clientAddress);
          formData.append('inspectionDate', uploadInspectionDate || '');
          const response = await fetch(apiUrl('/api/scraper/upload-image'), {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
          await readApiResponse(response);
          if (response.ok) {
            successCount.count++;
          } else {
            failedFiles.push(file.name);
          }
        } catch (err) {
          failedFiles.push(file.name);
        }
      }

      if (successCount.count > 0) {
        setMessage(
          `Successfully uploaded ${successCount.count} file(s)${
            failedFiles.length > 0 ? `. Failed: ${failedFiles.join(', ')}` : ''
          }`
        );
        setUploadFile(null);
        setUploadFiles([]);
        setUploadClientName('');
        setUploadClientAddress('');
        setUploadInspectionDate('');
        setShowUploadForm(false);
        await loadClients();
        if (selectedClient) {
          const updatedClient = clients.find((c) => c.id === selectedClient.id);
          if (updatedClient) handleClientClick(updatedClient);
        }
      } else {
        setError('Failed to upload images. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Failed to upload images');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setEditClientName(client.clientName);
    setEditClientAddress(client.clientAddress || '');
    setEditCompanycamUrl(client.companycamUrl || '');
  };

  const handleSaveClient = async () => {
    if (!editingClient) return;
    try {
      const response = await fetch(apiUrl('/api/scraper/update-client'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          clientId: editingClient.id,
          clientName: editClientName,
          clientAddress: editClientAddress,
          companycamUrl: editCompanycamUrl,
        }),
      });
      const data = await readApiResponse(response);
      if (!response.ok) { setError(data.message || 'Failed to update client'); return; }
      setMessage('Client updated successfully');
      setEditingClient(null);
      await loadClients();
    } catch (err) {
      setError(err.message || 'Failed to update client');
      console.error(err);
    }
  };

  const handleDeleteClient = async (client) => {
    if (!user?.isAdmin) { setError('Only admins can delete clients'); return; }
    if (!window.confirm(`Delete client "${client.clientName}" and all of its images? This cannot be undone.`)) return;
    try {
      const response = await fetch(apiUrl('/api/scraper/delete-client'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: client.id }),
      });
      const data = await readApiResponse(response);
      if (!response.ok) { setError(data.message || 'Failed to delete client'); return; }
      setMessage('Client deleted successfully');
      if (selectedClient?.id === client.id) { setSelectedClient(null); setClientImages([]); }
      await loadClients();
    } catch (err) {
      setError(err.message || 'Failed to delete client');
      console.error(err);
    }
  };

  // The preview modal browses the same flattened, date-grouped order the grid renders in, so
  // "next/prev" always matches what the user visually saw before opening the image.
  const flattenedClientImages = () => groupImagesByDate(clientImages).flatMap(([, imgs]) => imgs);

  const handlePreviewImage = (image) => {
    const flat = flattenedClientImages();
    const idx = flat.findIndex((img) => img.id === image.id);
    setPreviewIndex(idx >= 0 ? idx : 0);
    setShowPreview(true);
  };
  const closePreview = () => { setShowPreview(false); };
  const previewNext = () => {
    const total = flattenedClientImages().length;
    if (total > 0) setPreviewIndex((i) => (i + 1) % total);
  };
  const previewPrev = () => {
    const total = flattenedClientImages().length;
    if (total > 0) setPreviewIndex((i) => (i - 1 + total) % total);
  };

  const handleRotateImage = async (direction) => {
    const flat = flattenedClientImages();
    const image = flat[previewIndex];
    if (!image) return;

    const current = image.rotation || 0;
    const next = ((current + (direction === 'left' ? -90 : 90)) % 360 + 360) % 360;

    setClientImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, rotation: next } : img)));

    try {
      const response = await fetch(apiUrl('/api/scraper/image-rotation'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageId: image.id, rotation: next }),
      });
      const data = await readApiResponse(response);
      if (!response.ok) throw new Error(data.message || 'Failed to rotate image');
    } catch (err) {
      // Revert the optimistic update on failure.
      setClientImages((prev) => prev.map((img) => (img.id === image.id ? { ...img, rotation: current } : img)));
      setError(err.message || 'Failed to rotate image');
    }
  };

  // Left/Right browse photos, Escape closes — active only while the preview modal is open.
  useEffect(() => {
    if (!showPreview) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') previewNext();
      else if (e.key === 'ArrowLeft') previewPrev();
      else if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview, clientImages, previewIndex]);

  const fetchClientImages = async (clientId) => {
    const response = await fetch(apiUrl(`/api/scraper/images?clientId=${clientId}`), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await readApiResponse(response);
    if (!response.ok) {
      throw new Error(data.message || 'Failed to load images');
    }
    return data.images?.filter((img) => img.clientId === clientId) || [];
  };

  const handleClientClick = async (client) => {
    setSelectedClient(client);
    setShowShareMenu(false);
    setLoading(true);
    setError('');
    try {
      setClientImages(await fetchClientImages(client.id));
    } catch (err) {
      setError(err.message || 'Failed to load images');
      setClientImages([]);
      console.error('Error loading images:', err);
    } finally {
      setLoading(false);
      navigate(`/projects/client/${client.id}`);
    }
  };

  const handleRefreshImages = async () => {
    if (!selectedClient) return;
    setRefreshingImages(true);
    setError('');
    try {
      setClientImages(await fetchClientImages(selectedClient.id));
    } catch (err) {
      setError(err.message || 'Failed to load images');
      console.error('Error refreshing images:', err);
    } finally {
      setRefreshingImages(false);
    }
  };

  const groupImagesByDate = (images) => {
    const grouped = {};
    images.forEach((image) => {
      const date = image.inspectionDate || 'Undated';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(image);
    });
    return Object.entries(grouped).sort((a, b) => {
      if (a[0] === 'Undated') return 1;
      if (b[0] === 'Undated') return -1;
      return new Date(b[0]) - new Date(a[0]);
    });
  };

  const handleBackToList = () => { setSelectedClient(null); setClientImages([]); navigate('/projects'); };

  const toggleUploadForm = () => setShowUploadForm((v) => !v);

  // Timeline link = our own public, no-login gallery page for this client (generated/fetched
  // on demand). Project link = this app's own client page (requires login to view).
  const handleShareTimelineLink = async () => {
    if (!selectedClient) return;
    setShowShareMenu(false);
    try {
      const response = await fetch(apiUrl('/api/scraper/share-link'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: selectedClient.id }),
      });
      const data = await readApiResponse(response);
      if (!response.ok) { setError(data.message || 'Failed to create timeline link'); return; }
      const fullUrl = `${window.location.origin}${data.url}`;
      await navigator.clipboard.writeText(fullUrl).catch(() => {});
      setMessage(`Timeline link: ${fullUrl}`);
    } catch (err) {
      setError(err.message || 'Failed to create timeline link');
      console.error(err);
    }
  };

  const handleShareProjectLink = async () => {
    if (!selectedClient) return;
    setShowShareMenu(false);
    const fullUrl = `${window.location.origin}/projects/client/${selectedClient.id}`;
    await navigator.clipboard.writeText(fullUrl).catch(() => {});
    setMessage(`Project link: ${fullUrl}`);
  };

  // The original CompanyCam page this client was scraped from — only exists for clients linked
  // via "Add from CompanyCam" (or auto-linked by the CompanyCam sync), never for manually-created
  // ones, hence the companycamUrl guard everywhere this is used.
  const handleShareCompanyCamLink = async () => {
    if (!selectedClient?.companycamUrl) return;
    setShowShareMenu(false);
    await navigator.clipboard.writeText(selectedClient.companycamUrl).catch(() => {});
    setMessage(`CompanyCam link: ${selectedClient.companycamUrl}`);
  };

  // Shared by both the client-list view and the client-detail view — the form itself already
  // branches on whether selectedClient is set (see the "Client" vs Name/Address fields below).
  const renderUploadPanel = () => (
    <div className="cc-panel">
      <div className="cc-panel-header">
        <div>
          <div className="cc-panel-title">Upload Image</div>
          <div className="cc-panel-sub">Upload images directly from your device</div>
        </div>
        <button className="cc-panel-close" onClick={() => setShowUploadForm(false)}>&#x2715;</button>
      </div>
      <form onSubmit={handleImageUpload} className="cc-form">
        <div className="cc-form-group">
          <label className="cc-form-label" htmlFor="image-files">{videoEnabled ? 'Select Photos or Videos (Multiple)' : 'Select Images (Multiple)'}</label>
          <input
            id="image-files"
            type="file"
            accept={videoEnabled ? 'image/*,video/*' : 'image/*'}
            multiple
            className="cc-file-input"
            onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
            disabled={loading}
          />
          {uploadFiles.length > 0 && (
            <div className="cc-file-list">
              <div className="cc-file-count">{uploadFiles.length} file(s) selected</div>
              <ul>
                {uploadFiles.map((file, idx) => (
                  <li key={idx}>
                    {file.name}
                    <button
                      type="button"
                      className="cc-remove-file"
                      onClick={() => setUploadFiles(uploadFiles.filter((_, i) => i !== idx))}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <span className="cc-form-hint">{videoEnabled ? 'Photos: JPEG, PNG, GIF, WebP (max 50 MB). Videos: MP4, MOV, WebM (max 2 GB)' : 'Supported: JPEG, PNG, GIF, WebP. Max 50MB per file'}</span>
        </div>
        {!selectedClient && (
          <div className="cc-form-row">
            <div className="cc-form-group cc-suggestion-wrapper">
              <label className="cc-form-label" htmlFor="upload-client-name">Client Name</label>
              <input
                id="upload-client-name"
                type="text"
                className="cc-form-input"
                value={uploadClientName}
                onChange={(e) => handleClientNameChange(e.target.value)}
                onFocus={() => uploadClientName && setShowNameSuggestions(true)}
                placeholder="Enter or select client name"
                disabled={loading}
                autoComplete="off"
              />
              {showNameSuggestions && nameSuggestions.length > 0 && (
                <div className="cc-suggestions">
                  {nameSuggestions.map((name, idx) => (
                    <div
                      key={idx}
                      className="cc-suggestion-item"
                      onClick={() => handleSelectNameSuggestion(name)}
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="cc-form-group cc-suggestion-wrapper">
              <label className="cc-form-label" htmlFor="upload-client-address">Client Address (Optional)</label>
              <input
                id="upload-client-address"
                type="text"
                className="cc-form-input"
                value={uploadClientAddress}
                onChange={(e) => handleClientAddressChange(e.target.value)}
                onFocus={() => uploadClientAddress && setShowAddressSuggestions(true)}
                placeholder="Enter or select address"
                disabled={loading}
                autoComplete="off"
              />
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div className="cc-suggestions">
                  {addressSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="cc-suggestion-item"
                      onClick={() => handleSelectAddressSuggestion(suggestion)}
                    >
                      {suggestion.source === 'history' && (
                        <span className="cc-suggestion-tag">Past client</span>
                      )}
                      {suggestion.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="cc-form-group">
          <label className="cc-form-label" htmlFor="inspection-date">Inspection Date (Optional)</label>
          <input
            id="inspection-date"
            type="date"
            className="cc-form-input"
            value={uploadInspectionDate}
            onChange={(e) => setUploadInspectionDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            disabled={loading}
          />
        </div>
        {selectedClient && (
          <div className="cc-form-group">
            <label className="cc-form-label">Client</label>
            <div className="cc-selected-client">
              <strong>{selectedClient.clientName}</strong>
              {selectedClient.clientAddress && <span>{selectedClient.clientAddress}</span>}
            </div>
          </div>
        )}
        <div className="cc-form-actions">
          <button type="submit" className="cc-submit-btn" disabled={loading || uploadFiles.length === 0}>
            {loading ? 'Uploading…' : `Upload ${uploadFiles.length} Image${uploadFiles.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="cc-page">
      <div className="cc-content">

        {!selectedClient ? (
          <>
            {/* Page header */}
            <div className="cc-page-header">
              <h1 className="cc-page-title">Projects</h1>
              <div className="cc-header-actions">
                <button
                  className={`cc-header-btn${showUploadForm ? ' cc-header-btn--active' : ''}`}
                  onClick={toggleUploadForm}
                >
                  <AiOutlinePlus size={15} />
                  Upload Image
                </button>
              </div>
            </div>

            {error && <div className="cc-msg cc-msg--error">{error}</div>}
            {message && <div className="cc-msg cc-msg--success">{message}</div>}

            {/* Camera modal removed - using floating button instead */}

            {/* Upload panel */}
            {showUploadForm && renderUploadPanel()}

            {/* Edit client modal */}
            {editingClient && (
              <div className="cc-modal-overlay" onClick={() => setEditingClient(null)}>
                <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="cc-modal-hdr">
                    <h3>Edit Client Details</h3>
                    <button className="cc-modal-close" onClick={() => setEditingClient(null)}>&#x2715;</button>
                  </div>
                  <div className="cc-modal-body">
                    <div className="cc-form-group">
                      <label className="cc-form-label">Client Name</label>
                      <input type="text" className="cc-form-input" value={editClientName} onChange={(e) => setEditClientName(e.target.value)} />
                    </div>
                    <div className="cc-form-group">
                      <label className="cc-form-label">Client Address</label>
                      <input type="text" className="cc-form-input" value={editClientAddress} onChange={(e) => setEditClientAddress(e.target.value)} />
                    </div>
                    <div className="cc-form-group">
                      <label className="cc-form-label">CompanyCam Link</label>
                      <input
                        type="url"
                        className="cc-form-input"
                        placeholder="https://app.companycam.com/projects/..."
                        value={editCompanycamUrl}
                        onChange={(e) => setEditCompanycamUrl(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="cc-modal-ftr">
                    <button className="cc-modal-cancel" onClick={() => setEditingClient(null)}>Cancel</button>
                    <button className="cc-modal-confirm" onClick={handleSaveClient}>Save Changes</button>
                  </div>
                </div>
              </div>
            )}

            {/* Clients list */}
            <div className="cc-clients-wrap">
              {/* Keep the header/search/sort visible whenever a search is active, even if it
                  currently matches nothing — only a truly client-less account (no search, no
                  results, nothing ever loaded) falls back to the "get started" empty state. */}
              {!clientsLoaded ? (
                <div className="cc-loading">
                  <div className="cc-spinner" />
                  Loading projects…
                </div>
              ) : (clients.length > 0 || clientSearch.trim()) ? (
                <>
                  <div className="cc-list-header">
                    <span className="cc-list-heading">
                      Projects
                      <span className="cc-list-count">{clientsActiveTotal.toLocaleString()}</span>
                      {clientsBackupTotal > 0 && (
                        <span className="cc-list-count cc-list-count--backup" title="Projects stored in the backup archive, listed after your Captureify projects">
                          <AiOutlineInbox size={11} />
                          {clientsBackupTotal.toLocaleString()} backup
                        </span>
                      )}
                    </span>
                    <div className="cc-list-actions">
                      <button
                        type="button"
                        className="cc-refresh-btn"
                        onClick={handleRefreshClients}
                        disabled={refreshingClients}
                        title="Refresh projects"
                      >
                        <AiOutlineReload size={15} className={refreshingClients ? 'cc-spin' : ''} />
                        Refresh
                      </button>
                    </div>
                  </div>
                  <div className="cc-clients-controls">
                    <input
                      type="text"
                      className="cc-client-search-input"
                      placeholder="Search projects by name or address..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                    />
                    <select
                      className="cc-client-sort-select"
                      value={clientSortBy}
                      onChange={(e) => setClientSortBy(e.target.value)}
                    >
                      <option value="recent">Recent</option>
                      <option value="name">Name (A-Z)</option>
                      <option value="images">Most Images</option>
                    </select>
                  </div>
                  {clients.length > 0 ? (
                    <div className="cc-clients-list">
                      {clients.map((client) => (
                        <div key={client.id} className="cc-client-row" onClick={() => handleClientClick(client)}>
                          <div className="cc-client-avatar">
                            {client.thumbnailUrl ? (
                              <img src={client.thumbnailUrl} alt={client.clientName} />
                            ) : (
                              client.clientName.charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="cc-client-info">
                            <div className="cc-client-name-row">
                              <span className="cc-client-name">{client.clientName}</span>
                              {client.storageStatus === 'legacy' && (
                                <span className="cc-backup-badge" title="Photos are stored in the backup archive">
                                  <AiOutlineInbox size={11} />
                                  Backup
                                </span>
                              )}
                              {client.companycamUrl && (
                                <span className="cc-companycam-badge" title="Linked to a CompanyCam project">
                                  <AiOutlineLink size={11} />
                                  CompanyCam
                                </span>
                              )}
                            </div>
                            {client.clientAddress && (
                              <span className="cc-client-address">
                                <a
                                  href={mapsUrl(client.clientAddress)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="cc-address-link"
                                  title="Open in Google Maps"
                                  /* The whole row opens the project, so the link must
                                     not take that click with it. */
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <AiOutlineEnvironment size={11} className="cc-address-pin" />
                                  {client.clientAddress}
                                </a>
                              </span>
                            )}
                            <span className="cc-client-meta">
                              {client.imageCount + (client.backupImageCount || 0)} image{client.imageCount + (client.backupImageCount || 0) !== 1 ? 's' : ''}&nbsp;&middot;&nbsp;
                              {new Date(client.updatedAt).toLocaleDateString()}
                            </span>
                            {uploadStatuses[client.id]?.inProgress && (
                              <span className="cc-upload-badge">
                                <AiOutlineReload size={11} className="cc-spin" />
                                Uploading {uploadStatuses[client.id].completed}/{uploadStatuses[client.id].total}
                              </span>
                            )}
                          </div>
                          <div className="cc-client-actions" onClick={(e) => e.stopPropagation()}>
                            <button className="cc-icon-btn" onClick={() => handleEditClient(client)} title="Edit client">
                              <AiOutlineEdit size={17} />
                            </button>
                            {user?.isAdmin && (
                              <button className="cc-icon-btn cc-icon-btn--danger" onClick={() => handleDeleteClient(client)} title="Delete client">
                                <AiOutlineDelete size={17} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cc-empty">
                      <p className="cc-empty-text">No clients match your search</p>
                    </div>
                  )}
                  {clients.length > 0 && clients.length < clientsTotal && (
                    <button
                      type="button"
                      className="cc-load-more-btn"
                      onClick={handleLoadMoreClients}
                      disabled={loadingMoreClients}
                    >
                      {loadingMoreClients ? 'Loading…' : `Load More (${clientsTotal - clients.length} remaining)`}
                    </button>
                  )}
                </>
              ) : (
                <div className="cc-empty">
                  <div className="cc-empty-icon"><AiOutlinePlus size={28} /></div>
                  <p className="cc-empty-text">No projects yet</p>
                  <p className="cc-empty-sub">Use "Upload Image" or the camera to get started.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Client detail view */
          <>
            <div className="cc-breadcrumb">
              <button className="cc-breadcrumb-back" onClick={handleBackToList}>
                <AiOutlineArrowLeft size={14} />
                Projects
              </button>
              <span className="cc-breadcrumb-sep">/</span>
              <span className="cc-breadcrumb-current">{selectedClient.clientName}</span>
            </div>

            <div className="cc-detail-header">
              <div className="cc-detail-header-main">
                <div className="cc-detail-title-row">
                  <h1 className="cc-detail-title">{selectedClient.clientName}</h1>
                  {selectedClient.storageStatus === 'legacy' && (
                    <span className="cc-backup-badge" title="Photos are stored in the backup archive">
                      <AiOutlineInbox size={11} />
                      Backup
                    </span>
                  )}
                  {selectedClient.companycamUrl && (
                    <span className="cc-companycam-badge" title="Linked to a CompanyCam project">
                      <AiOutlineLink size={11} />
                      CompanyCam
                    </span>
                  )}
                  {/* Straight into the report builder with this project's photos
                      already loaded - no link to paste, nothing to scrape.
                      Reports are a manager/admin job, so technicians don't see it. */}
                  {(user?.isAdmin || user?.isManager) && reportToken && (
                    <a
                      href={reportifyUrl(selectedClient.id, reportToken)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cc-reportify-btn"
                      title="Open this project in Reportify and start a report"
                    >
                      <AiOutlineFileText size={14} />
                      Generate report
                    </a>
                  )}
                  <div className="cc-share-wrapper">
                    <button
                      className="cc-icon-btn"
                      title="Share"
                      aria-label="Share"
                      onClick={() => setShowShareMenu((v) => !v)}
                    >
                      <AiOutlineShareAlt size={16} />
                    </button>
                    {showShareMenu && (
                      <div className="cc-share-menu">
                        <button type="button" className="cc-share-menu-item" onClick={handleShareTimelineLink}>
                          <span className="cc-share-menu-item-title">Timeline Link</span>
                          <span className="cc-share-menu-item-sub">Public — no login required</span>
                        </button>
                        <button type="button" className="cc-share-menu-item" onClick={handleShareProjectLink}>
                          <span className="cc-share-menu-item-title">Project Link</span>
                          <span className="cc-share-menu-item-sub">Requires login to view</span>
                        </button>
                        {selectedClient.companycamUrl && (
                          <button type="button" className="cc-share-menu-item" onClick={handleShareCompanyCamLink}>
                            <span className="cc-share-menu-item-title">CompanyCam Link</span>
                            <span className="cc-share-menu-item-sub">The original CompanyCam timeline</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {selectedClient.clientAddress && (
                  <p className="cc-detail-address">
                    <a
                      href={mapsUrl(selectedClient.clientAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cc-address-link"
                      title="Open in Google Maps"
                    >
                      <AiOutlineEnvironment size={13} className="cc-address-pin" />
                      {selectedClient.clientAddress}
                    </a>
                  </p>
                )}
                {uploadStatuses[selectedClient.id]?.inProgress && (
                  <span className="cc-upload-badge">
                    <AiOutlineReload size={11} className="cc-spin" />
                    Uploading {uploadStatuses[selectedClient.id].completed}/{uploadStatuses[selectedClient.id].total} to permanent storage
                  </span>
                )}
              </div>
              <button
                type="button"
                className="cc-refresh-btn"
                onClick={handleRefreshImages}
                disabled={refreshingImages}
                title="Refresh images"
              >
                <AiOutlineReload size={15} className={refreshingImages ? 'cc-spin' : ''} />
                Refresh
              </button>
            </div>

            {/* Main photo with the project's notes beside it (stacked on phones). */}
            <div className="cc-hero">
            <button type="button" className="cc-main-photo" onClick={() => setShowMainPhotoPicker(true)}>
              {selectedClient.thumbnailUrl ? (
                <>
                  <img src={selectedClient.thumbnailUrl} alt={selectedClient.clientName} />
                  <span className="cc-main-photo-edit">
                    <AiOutlineEdit size={13} /> Change main photo
                  </span>
                </>
              ) : (
                <span className="cc-main-photo-placeholder">
                  <AiOutlineCamera size={22} />
                  Add Main Photo
                </span>
              )}
            </button>

            {/* Notes about the whole project (the per-photo notes live on each photo). */}
            <ProjectNotes clientId={selectedClient.id} token={token} />
            </div>

            {showMainPhotoPicker && (
              <div className="cc-modal-overlay" onClick={() => setShowMainPhotoPicker(false)}>
                <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="cc-modal-hdr">
                    <h3>Choose Main Photo</h3>
                    <button className="cc-modal-close" onClick={() => setShowMainPhotoPicker(false)}>&#x2715;</button>
                  </div>
                  <div className="cc-modal-body">
                    <button
                      type="button"
                      className="cc-capture-main-btn"
                      onClick={() => { setShowMainPhotoPicker(false); onOpenCamera(selectedClient); }}
                    >
                      <AiOutlineCamera size={16} />
                      Capture New Photo
                    </button>

                    {clientImages.length > 0 && (
                      <div className="cc-main-photo-grid">
                        {clientImages.filter((image) => image.imageUrl && image.mediaType !== 'video').map((image) => (
                          <button
                            key={image.id}
                            type="button"
                            className="cc-main-photo-option"
                            onClick={() => handleSelectMainPhoto(image)}
                          >
                            <img src={image.imageUrl} alt="" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {error && <div className="cc-msg cc-msg--error">{error}</div>}
            {message && <div className="cc-msg cc-msg--success">{message}</div>}

            {loading ? (
              <div className="cc-loading">
                <div className="cc-spinner" />
                <span>Loading images&hellip;</span>
              </div>
            ) : (
              <div className="cc-inspection-groups">
                {clientImages && clientImages.length > 0 ? (
                  groupImagesByDate(clientImages).map(([date, images]) => (
                    <div key={date} className="cc-inspection-group">
                      <div className="cc-date-header">{formatDateDisplay(date)}</div>
                      <div className="cc-images-grid">
                        {images.map((image) => (
                          <div key={image.id} className="cc-image-card">
                            <div className="cc-image-thumb">
                              {image.mediaType === 'video' ? (
                                <button
                                  type="button"
                                  className="cc-video-thumb"
                                  onClick={() => handlePreviewImage(image)}
                                  aria-label="Play video"
                                >
                                  {image.imageUrl ? (
                                    <img src={image.imageUrl} alt="" loading="lazy" />
                                  ) : (
                                    <AiOutlineVideoCamera size={30} className="cc-video-thumb-icon" />
                                  )}
                                  <span className="cc-video-play" aria-hidden="true" />
                                  {image.durationMs ? (
                                    <span className="cc-video-duration">{formatDuration(image.durationMs)}</span>
                                  ) : null}
                                </button>
                              ) : (
                              <img
                                src={image.imageUrl}
                                alt={selectedClient.clientName}
                                loading="lazy"
                                style={image.rotation ? { transform: `rotate(${image.rotation}deg)` } : undefined}
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3EError%3C/text%3E%3C/svg%3E';
                                }}
                                onLoad={() => { if (!isBackupImage(image) && !imageNotes[image.id]) loadImageNote(image.id); }}
                                onClick={() => handlePreviewImage(image)}
                              />
                              )}
                            </div>
                            {!!(noteFor(image) && noteFor(image).trim()) && (
                              <div className="cc-note-preview" title={noteFor(image)}>
                                {noteFor(image)}
                              </div>
                            )}
                            <div className="cc-image-actions">
                              <button className="cc-img-btn" onClick={() => handlePreviewImage(image)} title="Preview">
                                <AiOutlineEye size={15} />
                              </button>
                              <button
                                className="cc-img-btn"
                                onClick={() => handleDownloadImage(image)}
                                disabled={downloadingId === image.id}
                                title="Download"
                                aria-label={image.mediaType === 'video' ? 'Download video' : 'Download photo'}
                              >
                                <AiOutlineDownload size={15} />
                              </button>
                              {!isBackupImage(image) && (
                                <>
                                  <button className="cc-img-btn" onClick={() => openNotesModal(image)} title="Notes">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                  <button className="cc-img-btn cc-img-btn--danger" onClick={() => handleDeleteImage(image)} title="Remove">
                                    <AiOutlineDelete size={15} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="cc-no-images">No images for this client</div>
                )}
              </div>
            )}

            <button className="cc-fab cc-fab--primary" onClick={() => onOpenCamera(selectedClient)} title="Open camera" aria-label="Open camera">
              <AiOutlineCamera size={20} />
            </button>
          </>
        )}

        {/* Notes modal */}
        {notesModal.isOpen && notesModal.image && (
          <div className="cc-modal-overlay cc-notes-overlay" onClick={closeNotesModal}>
            <div className="cc-notes-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cc-modal-hdr">
                <h3>Edit Notes</h3>
                <button className="cc-modal-close" onClick={closeNotesModal}>&#x2715;</button>
              </div>
              <div className="cc-notes-body">
                <div className="cc-notes-img-pane">
                  <img src={notesModal.image.mediaType === 'video' ? notesModal.image.imageUrl : (notesModal.image.fullImageUrl || notesModal.image.imageUrl)} alt="Current" className="cc-notes-img" />
                </div>
                <div className="cc-notes-form-pane">
                  <div className="cc-notes-form-label">Notes</div>
                  <textarea
                    className="cc-notes-textarea"
                    placeholder="Add your notes here... (max 500 characters)"
                    value={imageNotes[notesModal.imageId] || ''}
                    onChange={(e) => handleNoteChange(notesModal.imageId, e.target.value)}
                    maxLength={500}
                  />
                  <div className="cc-notes-footer">
                    <span className="cc-notes-count">{(imageNotes[notesModal.imageId] || '').length} / 500</span>
                    <div className="cc-notes-btns">
                      <button className="cc-notes-cancel" onClick={closeNotesModal}>Cancel</button>
                      <button className="cc-notes-save" onClick={saveNoteFromModal} disabled={savingNotes[notesModal.imageId]}>
                        {savingNotes[notesModal.imageId] ? 'Saving\u2026' : 'Save Note'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Success/Error Messages - Below buttons */}
              {message && (
                <div className="cc-notes-message cc-notes-success">
                  ✓ {message}
                </div>
              )}
              {error && (
                <div className="cc-notes-message cc-notes-error">
                  ✕ {error}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Image preview modal — browsable (arrows/keyboard) and rotatable */}
        {showPreview && (() => {
          const flat = flattenedClientImages();
          const previewImage = flat[previewIndex];
          if (!previewImage) return null;

          return (
            <div className="cc-preview-overlay" onClick={closePreview}>
              {flat.length > 1 && (
                <button
                  className="cc-preview-nav cc-preview-nav--prev"
                  onClick={(e) => { e.stopPropagation(); previewPrev(); }}
                  title="Previous photo"
                  aria-label="Previous photo"
                >
                  &#x2039;
                </button>
              )}

              <div className="cc-preview-modal" onClick={(e) => e.stopPropagation()}>
                <button className="cc-preview-close" onClick={closePreview}>&#x2715;</button>
                <button
                  className="cc-preview-download"
                  onClick={() => handleDownloadImage(previewImage)}
                  disabled={downloadingId === previewImage.id}
                  title="Download"
                  aria-label="Download photo"
                >
                  <AiOutlineDownload size={17} />
                </button>
                {!isBackupImage(previewImage) && previewImage.mediaType !== 'video' && (
                  <div className="cc-preview-rotate-controls">
                    <button className="cc-preview-rotate-btn" onClick={() => handleRotateImage('left')} title="Rotate left">
                      &#x21B6;
                    </button>
                    <button className="cc-preview-rotate-btn" onClick={() => handleRotateImage('right')} title="Rotate right">
                      &#x21B7;
                    </button>
                  </div>
                )}
                {/* resetKey is image id + rotation, so switching photos (or rotating one)
                    starts fresh instead of carrying the previous transform. */}
                {previewImage.mediaType === 'video' ? (
                  <div className="cc-preview-video-stage" onClick={closePreview}>
                    <video
                      key={previewImage.id}
                      className="cc-preview-video"
                      src={previewImage.fullImageUrl}
                      poster={previewImage.imageUrl || undefined}
                      controls
                      playsInline
                      autoPlay
                      preload="metadata"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                ) : (
                <ZoomableImage
                  resetKey={`${previewImage.id}-${previewImage.rotation || 0}`}
                  src={previewImage.fullImageUrl || previewImage.imageUrl}
                  alt="Preview"
                  rotation={previewImage.rotation || 0}
                  onBackdropClick={closePreview}
                />
                )}
                {flat.length > 1 && (
                  <div className="cc-preview-counter">{previewIndex + 1} / {flat.length}</div>
                )}
              </div>

              {flat.length > 1 && (
                <button
                  className="cc-preview-nav cc-preview-nav--next"
                  onClick={(e) => { e.stopPropagation(); previewNext(); }}
                  title="Next photo"
                  aria-label="Next photo"
                >
                  &#x203A;
                </button>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default CompanyCamPage;
